import { createContext, useCallback, useContext, useState } from "react";

import { useCodeMirrorViewContext } from "../codemirror-view-context.js";
import { useTableContext } from "./table-context.js";

const EditingContext = createContext(undefined);

export function EditingContextProvider({ children }) {
    const { table } = useTableContext();
    const view = useCodeMirrorViewContext();
    const [cellData, setCellData] = useState(null);
    const [initialContent, setInitialContent] = useState(undefined);

    const write = useCallback(
        (rowIndex, cellIndex, content) => {
            const { from, to } = table.getCell(rowIndex, cellIndex);
            const currentText = view.state.sliceDoc(from, to);
            if (currentText !== initialContent && initialContent !== undefined) {
                return;
            }
            setInitialContent(undefined);
            view.dispatch({ changes: { from, to, insert: content } });
            view.requestMeasure();
            setCellData(null);
        },
        [initialContent, table, view],
    );

    const commitCellData = useCallback(() => {
        if (!cellData) {
            return;
        }
        if (!cellData.dirty) {
            setCellData(null);
            setInitialContent(undefined);
            return;
        }
        write(cellData.rowIndex, cellData.cellIndex, cellData.content);
    }, [cellData, write]);

    const cancelEditing = useCallback(() => setCellData(null), []);

    const startEditing = useCallback(
        (rowIndex, cellIndex, initialValue) => {
            if (cellData?.dirty) {
                commitCellData();
            }
            setInitialContent(initialValue);
            setCellData({
                rowIndex,
                cellIndex,
                content: table.getCell(rowIndex, cellIndex).content.trim(),
                dirty: false,
            });
        },
        [cellData, commitCellData, table],
    );

    const updateCellData = useCallback((content) => {
        setCellData((prev) => (prev ? { ...prev, content, dirty: true } : prev));
    }, []);

    const clearCells = useCallback(
        (selection) => {
            const changes = [];
            const { minX, minY, maxX, maxY } = selection.normalized();
            for (let row = minY; row <= maxY; row += 1) {
                for (let cell = minX; cell <= maxX; cell += 1) {
                    const { from, to } = table.getCell(row, cell);
                    changes.push({ from, to, insert: "" });
                }
            }
            view.dispatch({ changes });
        },
        [table, view],
    );

    return (
        <EditingContext.Provider
            value={{
                cellData,
                updateCellData,
                cancelEditing,
                commitCellData,
                clearCells,
                startEditing,
            }}
        >
            {children}
        </EditingContext.Provider>
    );
}

export function useEditingContext() {
    const context = useContext(EditingContext);
    if (context === undefined) {
        throw new Error("useEditingContext is only available inside EditingContext.Provider");
    }
    return context;
}
